import { IonCard, useIonAlert, useIonRouter } from '@ionic/react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '../../components/Button'
import ContactPartner from '../../components/Contact/ContactPartner'
import { dayLabel, messageTime } from '../../components/Contact/format'
import Content from '../../components/Content'
import Header from '../../components/Header'
import InboxTabs from '../../components/Inbox/InboxTabs'
import Page from '../../components/Page'
import type { ContactRequestWithPeopleFragment } from '../../lib/apollo/types'
import {
    ContactRequestDirection,
    ContactRequestStatus,
    ContactTargetType,
    useAcceptContactRequestMutation,
    useContactRequestsWithPeopleQuery,
    useDeclineContactRequestMutation,
    useWithdrawContactRequestMutation,
} from '../../lib/apollo/types'
import useToast from '../../providers/Toast/hooks'

import './style.scss'

/**
 * Auch die schlanke Badge-Abfrage muss mitlaufen, sonst zeigt die Tab-Leiste
 * nach dem Annehmen weiter die alte Zahl.
 */
const REFETCH = ['contactRequestsWithPeople', 'contactRequests']

/**
 * Annehmen und Ablehnen sitzen hier und nicht in der Unterhaltung: Nur hier
 * liegt die Request-ID vor, und die Entscheidung gehoert vor den Chat, nicht
 * hinein.
 */
const ContactRequests: React.FC = () => {
    const { t } = useTranslation()
    const [present] = useToast()
    const [confirm] = useIonAlert()
    const router = useIonRouter()

    const { data: incoming, loading: incomingLoading } =
        useContactRequestsWithPeopleQuery({
            variables: {
                status: ContactRequestStatus.Pending,
                direction: ContactRequestDirection.Incoming,
            },
        })

    const { data: outgoing, loading: outgoingLoading } =
        useContactRequestsWithPeopleQuery({
            variables: {
                status: ContactRequestStatus.Pending,
                direction: ContactRequestDirection.Outgoing,
            },
        })

    const [accept] = useAcceptContactRequestMutation({
        refetchQueries: REFETCH,
    })
    const [decline] = useDeclineContactRequestMutation({
        refetchQueries: REFETCH,
    })
    // Zurueckziehen entfernt die ganze Unterhaltung, nicht nur die Anfrage —
    // sonst stuende sie im Posteingang weiter und liefe beim Oeffnen ins Leere.
    const [withdraw] = useWithdrawContactRequestMutation({
        refetchQueries: [...REFETCH, 'contactConversations', 'contactUnread'],
    })

    const onAccept = useCallback(
        async (request: ContactRequestWithPeopleFragment) => {
            await accept({ variables: { id: request.id } })
            present(
                t('contact.request.accepted', 'Anfrage angenommen.'),
                'success',
            )
            router.push(`/inbox/conversations/${request.conversationId}`)
        },
        [accept, present, t, router],
    )

    const onDecline = useCallback(
        async (request: ContactRequestWithPeopleFragment) => {
            await decline({ variables: { id: request.id } })
            present(t('contact.request.declined', 'Anfrage abgelehnt.'), 'success')
        },
        [decline, present, t],
    )

    // Zurueckziehen loescht die Anfrage samt Nachricht auch beim Empfaenger —
    // ein Fehlgriff waere nicht mehr zu heilen, deshalb die Nachfrage.
    const onWithdraw = useCallback(
        (request: ContactRequestWithPeopleFragment) => {
            confirm({
                header: t('contact.request.withdraw', 'Zurückziehen'),
                message: t(
                    'contact.request.withdrawConfirm',
                    'Die Anfrage und deine Nachricht werden auch beim Empfänger gelöscht. Das lässt sich nicht rückgängig machen.',
                ),
                buttons: [
                    { text: t('buttons.cancel'), role: 'cancel' },
                    {
                        text: t('contact.request.withdraw', 'Zurückziehen'),
                        handler: async () => {
                            await withdraw({ variables: { id: request.id } })
                            present(
                                t(
                                    'contact.request.withdrawn',
                                    'Anfrage zurückgezogen.',
                                ),
                                'success',
                            )
                        },
                    },
                ],
            })
        },
        [withdraw, present, t, confirm],
    )

    /**
     * Woher die Anfrage kommt, ist die halbe Entscheidung — "ueber die Talent
     * Card X" beantwortet die Frage, warum jemand ausgerechnet mich anschreibt.
     */
    const context = useCallback(
        (request: ContactRequestWithPeopleFragment) => {
            const parts = []

            if (request.targetType === ContactTargetType.Talent) {
                parts.push(
                    request.target?.name
                        ? t('contact.request.viaTalent', {
                            defaultValue: 'über die Talent Card {{name}}',
                            name: request.target.name,
                        })
                        : t('contact.request.viaTalentUnknown', 'über eine Talent Card'),
                )
            }

            parts.push(
                `${dayLabel(request.createdAt, t)}, ${messageTime(request.createdAt)}`,
            )

            return parts.join(' · ')
        },
        [t],
    )

    const incomingRequests = incoming?.contactRequests ?? []
    const outgoingRequests = outgoing?.contactRequests ?? []

    return (
        <Page trackingTitle='Contact Requests'>
            <Header
                buttonLeft='menu'
                title={t('title.inbox', 'Postfach')}
            />
            <Content>
                <InboxTabs
                    active='requests'
                    requestCount={incomingRequests.length}
                />
                <div className='contact-requests ion-padding'>
                    <h2 className='contact-requests__headline'>
                        {t('contact.requests.incoming', 'Erhalten')}
                    </h2>
                    {!incomingLoading && incomingRequests.length === 0 && (
                        <p className='contact-requests__empty'>
                            {t(
                                'contact.requests.incoming.empty',
                                'Keine offenen Anfragen.',
                            )}
                        </p>
                    )}
                    {incomingRequests.map((request) => (
                        <IonCard
                            key={request.id}
                            className='contact-requests__item'
                        >
                            <ContactPartner
                                name={
                                    request.fromUser
                                        ? `${request.fromUser.firstName} ${request.fromUser.lastName}`
                                        : undefined
                                }
                                image={request.fromUser?.image}
                                subline={context(request)}
                            />
                            <p className='n2br contact-requests__message'>
                                {request.message}
                            </p>
                            <div className='contact-requests__actions'>
                                <Button
                                    size='small'
                                    fill='solid'
                                    color='secondary'
                                    onClick={() => onAccept(request)}
                                >
                                    {t('contact.request.accept', 'Annehmen')}
                                </Button>
                                <Button
                                    size='small'
                                    fill='clear'
                                    onClick={() => onDecline(request)}
                                >
                                    {t('contact.request.decline', 'Ablehnen')}
                                </Button>
                            </div>
                        </IonCard>
                    ))}

                    <h2 className='contact-requests__headline'>
                        {t('contact.requests.outgoing', 'Gesendet')}
                    </h2>
                    {!outgoingLoading && outgoingRequests.length === 0 && (
                        <p className='contact-requests__empty'>
                            {t(
                                'contact.requests.outgoing.empty',
                                'Keine offenen Anfragen.',
                            )}
                        </p>
                    )}
                    {outgoingRequests.map((request) => (
                        <IonCard
                            key={request.id}
                            className='contact-requests__item'
                        >
                            <ContactPartner
                                name={request.target?.name}
                                image={request.target?.image}
                                subline={`${dayLabel(request.createdAt, t)}, ${messageTime(request.createdAt)}`}
                            />
                            <p className='n2br contact-requests__message'>
                                {request.message}
                            </p>
                            <div className='contact-requests__actions'>
                                <Button
                                    size='small'
                                    fill='solid'
                                    color='secondary'
                                    onClick={() =>
                                        router.push(
                                            `/inbox/conversations/${request.conversationId}`,
                                        )
                                    }
                                >
                                    {t('contact.request.open', 'Öffnen')}
                                </Button>
                                <Button
                                    size='small'
                                    fill='clear'
                                    onClick={() => onWithdraw(request)}
                                >
                                    {t(
                                        'contact.request.withdraw',
                                        'Zurückziehen',
                                    )}
                                </Button>
                            </div>
                        </IonCard>
                    ))}
                </div>
            </Content>
        </Page>
    )
}

export default ContactRequests
